import React, { useEffect, useState } from 'react';
import ChartContainer, {
  GeneralChartContainerProps
} from '../../Chart/ChartContainer/ChartContainer';
import {
  BasicXAxis,
  DataSource,
  Panel,
  PanelGlobalConfig,
  PanelGlobalConfigMap,
  PanelType,
  SampleSize
} from '@experiment-management-shared/types';
import {
  ACTIVE_FETCH_INTERVAL,
  AUTOGENERATED_PANELS_GLOBAL_CONFIG_MAP,
  BUILT_IN_CHART_TYPES,
  defaultFormFields,
  INTERNAL_CHART_TYPES
} from '@experiment-management-shared/constants';

import useExperimentsPanelData from '@experiment-management-shared/api/useExperimentsPanelData';
import usePanelSampleSize from '@experiment-management-shared/hooks/usePanelSampleSize';
import { calculateLockedState } from '@experiment-management-shared';

const LINE_TYPE = BUILT_IN_CHART_TYPES['BuiltIn/Line'] as PanelType;
const BAR_TYPE = BUILT_IN_CHART_TYPES['BuiltIn/Bar'] as PanelType;

type AutoGeneratedMetricsPanelProps = {
  chartName: string;
  metricName: string;
  containerProps: GeneralChartContainerProps;
  experimentKeys: string[];
  sampleSize: number;
  sampleSizes: SampleSize[];
  onPanelChange: (panel: Partial<Panel>) => void;
  panelGlobalConfig: PanelGlobalConfig;
  chartId: string;
  fetchFull: boolean;
  activeIntervalFetchDelay: number;
};

const EMPTY_HIDDEN_ITEMS: string[] = [];

const createLineChart = (
  xAxis: BasicXAxis,
  metricName: string,
  locked: boolean,
  chartId: string
) => {
  return {
    ...defaultFormFields[LINE_TYPE],
    chartType: LINE_TYPE,
    selectedYAxis: metricName,
    selectedXAxis: xAxis,
    metricNames: [metricName],
    locked,
    autoGenerated: true,
    chartId
  } as Panel;
};

const createBarChart = (metricName: string, chartId: string) => {
  return {
    ...defaultFormFields[BAR_TYPE],
    chartType: BAR_TYPE,
    metrics: [
      {
        name: metricName,
        aggregation: 'last'
      }
    ],
    metricNames: [metricName],
    locked: false,
    autoGenerated: true,
    chartId
  } as Panel;
};

const AutoGeneratedMetricsPanel = ({
  chartName,
  chartId,
  containerProps,
  metricName,
  experimentKeys,
  panelGlobalConfig,
  fetchFull,
  sampleSize,
  sampleSizes,
  onPanelChange,
  activeIntervalFetchDelay = ACTIVE_FETCH_INTERVAL
}: AutoGeneratedMetricsPanelProps) => {
  const chartType = INTERNAL_CHART_TYPES.autoGeneratedMetrics as PanelType;
  const [xAxis, setXAxis] = useState<BasicXAxis | null>(null);
  const [multipleDataPoints, setMultipleDataPoints] = useState(false);
  const { computedSampleSize } = usePanelSampleSize({
    sampleSize,
    sampleSizes
  });

  const { data: panelData } = useExperimentsPanelData(
    {
      experimentKeys,
      fetchFull,
      metricNames: [metricName],
      params: [],
      sampleSize: computedSampleSize,
      type: chartType
    },
    {
      staleTime: activeIntervalFetchDelay
    }
  );

  useEffect(() => {
    if (panelData && metricName) {
      const experiments = panelData.experiments as DataSource[];
      let hasSteps = false;
      let hasEpochs = false;
      let hasMultipleDataPoints = false;

      Object.values(experiments).forEach(experimentData => {
        const experimentMetric = experimentData.metrics.find(
          metric => metric.metricName === metricName
        );

        if ((experimentMetric?.values?.length || 0) > 1) {
          hasMultipleDataPoints = true;
        }

        if (experimentMetric) {
          if (!hasSteps) {
            hasSteps = experimentMetric?.steps?.some(step => step !== null);
          }

          if (!hasEpochs && !hasSteps) {
            hasEpochs = experimentMetric?.epochs?.some(epoch => epoch !== null);
          }
        }
      });

      setXAxis(() => {
        if (hasSteps) {
          return 'step';
        }

        if (hasEpochs) {
          return 'epoch';
        }

        return 'duration';
      });

      setMultipleDataPoints(hasMultipleDataPoints);
    }
  }, [metricName, panelData]);

  // convert into a line chart / bar chart
  useEffect(() => {
    if (xAxis && metricName) {
      const type = multipleDataPoints ? LINE_TYPE : BAR_TYPE;

      const locked = panelGlobalConfig
        ? calculateLockedState(
            panelGlobalConfig,
            {
              chartId,
              chartType: type,
              selectedXAxis: xAxis
            },
            AUTOGENERATED_PANELS_GLOBAL_CONFIG_MAP as PanelGlobalConfigMap
          )
        : false;

      onPanelChange(
        type === LINE_TYPE
          ? createLineChart(xAxis, metricName, locked, chartId)
          : createBarChart(metricName, chartId)
      );
    }
  }, [
    chartId,
    multipleDataPoints,
    onPanelChange,
    xAxis,
    metricName,
    panelGlobalConfig
  ]);

  return (
    <ChartContainer
      {...containerProps}
      isLoading
      hasData
      isPreviousData
      isFetching={false}
      isError={false}
      title={chartName}
      hiddenMenuItems={EMPTY_HIDDEN_ITEMS}
    >
      <div />
    </ChartContainer>
  );
};

AutoGeneratedMetricsPanel.CONFIG_PROPERTIES = [
  'metricName',
  'chartName',
  'experimentKeys',
  'panelGlobalConfig',
  'sampleSize',
  'onPanelChange',
  'chartId',
  'fetchFull',
  'activeIntervalFetchDelay'
];

export default AutoGeneratedMetricsPanel;
